import { docRefSchema } from '@lms/shared-types'
import type { DocRef } from '@lms/shared-types'
import { ApiError } from './client'

const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '')

function buildUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${API_BASE}${normalized}`
}

function extractErrorMessage(payload: unknown, status: number): string {
  if (
    typeof payload === 'object' &&
    payload !== null &&
    'error' in payload &&
    typeof (payload as { error: unknown }).error === 'string'
  ) {
    return (payload as { error: string }).error
  }
  return `Request failed with status ${status}`
}

async function parseJson(response: Response): Promise<unknown> {
  const text = await response.text()
  if (text.length === 0) return null
  try {
    return JSON.parse(text) as unknown
  } catch {
    return text
  }
}

/**
 * POST `/api/v1/leaves/:leaveId/documents` — multipart upload.
 *
 * Returns the `DocRef` for the newly-uploaded document.
 * Throws `ApiError` with the original status for 4xx/5xx responses so
 * callers can map status codes to user-friendly messages.
 */
export async function uploadDocument(
  leaveId: string,
  file: File,
): Promise<DocRef> {
  const form = new FormData()
  form.append('file', file)

  const response = await fetch(
    buildUrl(`/api/v1/leaves/${encodeURIComponent(leaveId)}/documents`),
    {
      method: 'POST',
      credentials: 'include',
      body: form,
    },
  )

  const payload = await parseJson(response)

  if (!response.ok) {
    throw new ApiError(
      extractErrorMessage(payload, response.status),
      response.status,
      payload,
    )
  }

  // Envelope: { success: true, data: DocRef }
  const envelope =
    typeof payload === 'object' &&
    payload !== null &&
    'data' in payload
      ? (payload as { data: unknown }).data
      : payload

  return docRefSchema.parse(envelope)
}

/**
 * DELETE `/api/v1/leaves/:leaveId/documents/:documentId`.
 *
 * Owner can delete their own document; HR+ can delete any.
 */
export async function deleteDocument(
  leaveId: string,
  documentId: string,
): Promise<void> {
  const response = await fetch(
    buildUrl(
      `/api/v1/leaves/${encodeURIComponent(leaveId)}/documents/${encodeURIComponent(documentId)}`,
    ),
    {
      method: 'DELETE',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    },
  )

  if (!response.ok) {
    const payload = await parseJson(response)
    throw new ApiError(
      extractErrorMessage(payload, response.status),
      response.status,
      payload,
    )
  }
}

/**
 * Returns the api URL that streams the document bytes through the api pod.
 * The S3 URL is never exposed to the browser — the api pod fetches from S3
 * over a VPC gateway endpoint and serves the bytes inline. Opening this URL
 * in a new tab renders/downloads the file via standard cookie auth.
 */
export function getDocumentUrl(leaveId: string, documentId: string): string {
  return buildUrl(
    `/api/v1/leaves/${encodeURIComponent(leaveId)}/documents/${encodeURIComponent(documentId)}`,
  )
}


/** Read a File as base64 *without* the `data:...,` prefix. The api
 *  expects the raw base64; the prefix is added inside ai-service when
 *  it builds the data URL for the vision model. */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'))
    reader.onload = () => {
      const result = reader.result
      if (typeof result !== 'string') {
        reject(new Error('FileReader did not return a string'))
        return
      }
      const commaIdx = result.indexOf(',')
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result)
    }
    reader.readAsDataURL(file)
  })
}
